import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource
} from 'axios';
import { useState } from 'react';
import { authConfig } from 'src/app/infra/auth';
import { Storage } from './storage';

export type Response<T> = Promise<AxiosResponse<T>>;
export type Error = AxiosError;
export type Source = CancelTokenSource;
export type Axios<T> = [Response<T>, Source];

axios.interceptors.request.use((req) => {
  if (req.withCredentials) {
    const session = Storage.get<any>(authConfig.key);
    req.headers.Authorization = `Bearer ${session?.token}`;
  }
  return req;
});

export function httpClient<T>(params: AxiosRequestConfig): Axios<T> {
  const source = axios.CancelToken.source();
  return [
    axios.request<T>({ ...params, cancelToken: source.token }),
    source
  ];
}

export function httpClientError<T>(msg?: string): Axios<T> {
  const source = axios.CancelToken.source();
  return [
    new Promise((_, reject) => {
      setTimeout(
        () => reject({ response: { data: msg || 'Empty error message!' } }),
        2000
      );
    }),
    source
  ];
}

export interface Status {
  loading: boolean;
  error: string;
  pristine: boolean;
  valid: boolean;
}

export interface Options {
  defaultErrorMsg?: string;
}

const wrapper = (
  loading = false, error = '', pristine = true, valid = false
) => ({
  loading, error, pristine, valid
});

export function useWrapper<T>(initialValue?: T) {
  const [payload, setPayload] = useState<T | undefined>(initialValue);
  const [status, setStatus] = useState<Status>(wrapper());
  let source: Source | undefined;

  function exec(
    promise: Axios<T>,
    onSuccess = (res: T) => { },
    onError = (err: Error) => { },
    options?: Options
  ) {
    const [request, src] = promise;
    source = src;
    setStatus(wrapper(true, ''));
    request
      .then((response) => {
        setStatus(wrapper(false, '', false, true));
        if (response.status === 204) {
          onSuccess([] as never);
          setPayload([] as never);
        } else {
          onSuccess(response.data);
          setPayload(response.data);
        }
      })
      .catch(({ response }) => {
        onError(response);
        setStatus(wrapper(
          false,
          response?.data || options?.defaultErrorMsg,
          false
        ));
      });
  }

  function reset() {
    setStatus(wrapper());
    setPayload(undefined);
  }

  function cancel() {
    source && source.cancel();
  }

  return { payload, ...status, exec, reset, cancel };
}
